import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { openPlans } from '../../libs/platform';
import { Lock, Unlock } from 'lucide-react';
import LoadingPet from '../shared/LoadingPet';
import Progress from './Progress';
import EarlyProgress from './EarlyProgress';
import NoProgress from './NoProgress';
import ProgressButton from './components/ProgressButton';
import FloatingMascot from './components/FloatingMascot';
import BackgroundTetris from './components/BackgroundTetris';
import http from '../../libs/http';
import { markProgressOpened } from '../../libs/progressStorage';
import analytics from '../../libs/analytics';
import './style.css';

const FREE_PLAN_UNLOCK_ITEMS = [
  'sua evolução real',
  'sua confiança ao longo do tempo',
  'próximos passos claros',
];

function FreePlanProgress({ userName = 'você' }: { userName?: string }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a0a2e] via-[#16082a] to-[#0f0518] relative overflow-x-hidden">
      <BackgroundTetris edgeOnly />
      <div className="progress-inner">
        <motion.header
          className="progress-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex-shrink-0 pt-1">
            <FloatingMascot size="lg" />
          </div>
          <div>
            <p className="progress-header-greeting">Olá,</p>
            <h1 className="progress-header-name">{userName}</h1>
          </div>
        </motion.header>
        <div className="flex-1 flex flex-col justify-center">
          <motion.section className="text-center mb-6" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.7 }}>
            <motion.h1 className="progress-hero-title" style={{ textShadow: '0 0 24px rgba(255,255,255,0.2)' }} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3, duration: 0.6 }}>Seu progresso no Ataric</motion.h1>
          </motion.section>
          <motion.section className="text-center mb-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.6 }}>
            <p className="text-white text-lg leading-relaxed font-normal" style={{ color: '#e8e0f0' }}>Falar já é um avanço.<br />Perceber que você está evoluindo muda tudo.</p>
          </motion.section>
          <motion.section className="text-center mb-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.6 }}>
            <p className="text-white text-lg font-bold leading-relaxed drop-shadow-[0_0_20px_rgba(255,255,255,0.2)]" style={{ color: '#fff', textShadow: '0 0 20px rgba(255,255,255,0.15)' }}>No plano gratuito, você conversa.<br />Com a assinatura, você enxerga seu caminho.</p>
          </motion.section>
          <motion.section className="mb-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.6 }}>
            <div className="rounded-2xl p-6 border border-white/25 bg-white/5 backdrop-blur-sm">
              <p className="text-white text-base font-bold mb-4" style={{ color: '#fff' }}>Ao assinar, você desbloqueia:</p>
              <ul className="space-y-3">
                {FREE_PLAN_UNLOCK_ITEMS.map((label, i) => (
                  <li key={i} className="flex items-center gap-3 text-white/90">
                    <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg bg-white/10 border border-white/20"><Lock className="w-4 h-4 text-white/80" /></span>
                    <span className="font-medium">{label}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.section>
          <motion.section className="text-center mb-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.6 }}>
            <p className="text-white text-lg font-bold leading-relaxed drop-shadow-[0_0_20px_rgba(255,255,255,0.2)]" style={{ color: '#fff', textShadow: '0 0 20px rgba(255,255,255,0.15)' }}>Veja o que já consegue dizer.<br />Sinta sua confiança crescer semana a semana.</p>
          </motion.section>
        </div>
        <motion.div className="pb-8" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }}>
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <ProgressButton variant="cta" size="xl" className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 border-purple-500/50 shadow-xl shadow-purple-500/30" onClick={() => openPlans(navigate)}>
              <span className="flex items-center justify-center gap-3"><Unlock className="w-5 h-5" /><span className="font-bold tracking-wide">Quero desbloquear meu progresso</span></span>
            </ProgressButton>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}

// Relatórios de progresso atualizados a cada 7 dias (período semanal).
// NO PROGRESS: quem nunca falou (totalMessages === 0). Backend só conta mensagens role "user".
// EARLY PROGRESS: quem falou entre 1 e 2 dias (totalDaysPracticing 1 ou 2), em algum modo.
// PROGRESS: 3+ dias de prática (não precisa ser seguidos). Após aparecer uma vez, sempre Progress.
const EARLY_PROGRESS_MIN_DAYS = 1;
const EARLY_PROGRESS_MAX_DAYS = 2;
const MIN_DAYS_FOR_PROGRESS = 3;

interface ProgressData {
  totalMessages: number;
  conversationCount: number;
  totalDaysPracticing: number;
  firstMessageAt: string | null;
  lastMessageAt: string | null;
  weeklyStats: {
    messages: number;
    daysActive: number;
    estimatedMinutes: number;
  };
  estimatedMinutesTotal: number;
  achievementsFromConversations?: string[];
  noPracticeThisWeek?: boolean;
  nextStepChallenge?: { title: string; subtitle: string };
}

function ProgressPage({ user }: any) {
  const [data, setData] = useState<ProgressData | null>(null);
  const [loading, setLoading] = useState(true);

  const isFreePlan = user?.planId == null || String(user?.planId) === '1';
  const userName = user?.name || 'Aluno';

  // Marca relatório aberto para qualquer variante
  useEffect(() => {
    markProgressOpened();
  }, []);

  useEffect(() => {
    if (!user?.token) return;
    if (isFreePlan) {
      setLoading(false);
      return;
    }
    async function fetchProgress() {
      try {
        const url = `${process.env.REACT_APP_API_URL}/api/v1/conversations/progress`;
        const headers = { Authorization: user.token };
        const response = await http.get(url, headers);

        if (response && !response.error) {
          setData(response);
        } else {
          setData({
            totalMessages: 0,
            conversationCount: 0,
            totalDaysPracticing: 0,
            firstMessageAt: null,
            lastMessageAt: null,
            weeklyStats: { messages: 0, daysActive: 0, estimatedMinutes: 0 },
            estimatedMinutesTotal: 0,
          });
        }
      } catch (err) {
        console.error('Failed to fetch progress:', err);
        setData({
          totalMessages: 0,
          conversationCount: 0,
          totalDaysPracticing: 0,
          firstMessageAt: null,
          lastMessageAt: null,
          weeklyStats: { messages: 0, daysActive: 0, estimatedMinutes: 0 },
          estimatedMinutesTotal: 0,
        });
      } finally {
        setLoading(false);
      }
    }

    fetchProgress();
  }, [user?.token, isFreePlan]);

  // Plano gratuito: sempre a mesma tela de progresso bloqueado (sem fetch)
  if (isFreePlan) {
    analytics.progressViewed('free_plan');
    return (
      <div className="progress-page">
        <FreePlanProgress userName={userName} />
      </div>
    );
  }

  if (loading || !data) {
    return <LoadingPet size="large" />;
  }

  // NoProgress: usuário nunca falou em nenhum modo (zero mensagens de usuário no backend)
  if (data.totalMessages === 0) {
    analytics.progressViewed('no_progress');
    return (
      <div className="progress-page">
        <NoProgress userName={userName} />
      </div>
    );
  }

  // EarlyProgress: falou entre 1 e 2 dias (em algum modo)
  const days = data.totalDaysPracticing;
  if (days >= EARLY_PROGRESS_MIN_DAYS && days <= EARLY_PROGRESS_MAX_DAYS) {
    analytics.progressViewed('early_progress');
    return (
      <div className="progress-page">
        <EarlyProgress data={data} userName={userName} />
      </div>
    );
  }

  // Progress: 3+ dias de prática (atualizado a cada 7 dias; nunca volta para NoProgress/EarlyProgress)
  if (days >= MIN_DAYS_FOR_PROGRESS) {
    analytics.progressViewed('progress');
    return (
      <div className="progress-page">
        <Progress data={data} userName={userName} />
      </div>
    );
  }

  // totalMessages >= 1 mas totalDaysPracticing ainda 0 (edge case: mesmo dia) → EarlyProgress
  analytics.progressViewed('early_progress');
  return (
    <div className="progress-page">
      <EarlyProgress data={data} userName={userName} />
    </div>
  );
}

export default ProgressPage;
